import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
// @mui
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Pagination,
	Popover,
	Stack,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import NewsTable from "src/components/news/NewsTable";
import { del, get } from "src/utils/networkUtils";
import Scrollbar from "../components/scrollbar";

const ITEMS_PER_PAGE = 10;

export default function NewsListPage() {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [news, setNews] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [open, setOpen] = useState(null);
	const [openId, setOpenId] = useState(null);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [deleteNewsId, setDeleteNewsId] = useState(null);

	const fetchNews = async (page = 1) => {
		try {
			setLoading(true);
			const offset = (page - 1) * ITEMS_PER_PAGE;
			const response = await get(`/admin/news?offset=${offset}&limit=${ITEMS_PER_PAGE}`);
			setNews(response.news || []);
			setTotalCount(response.totalCount || 0);
		} catch (error) {
			console.error("Error fetching news:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchNews(currentPage);
	}, [currentPage]);

	const handlePageChange = (event, page) => {
		setCurrentPage(page);
	};

	const handleOpenMenu = (event) => {
		setOpen(event.currentTarget);
		setOpenId(event.currentTarget.id);
	};

	const handleCloseMenu = () => {
		setOpen(null);
		setOpenId(null);
	};

	const handleCreateNews = () => {
		navigate("/dashboard/news/add");
	};

	const handleEditNews = (id) => {
		navigate(`/dashboard/news/edit/${id}`);
		setOpen(null);
	};

	const handleDeleteNews = (id) => {
		setDeleteNewsId(id);
		setShowDeleteDialog(true);
		setOpen(null);
	};

	const confirmDeleteNews = async () => {
		try {
			setLoading(true);
			await del(`/admin/news/${deleteNewsId}`);
			setShowDeleteDialog(false);
			setDeleteNewsId(null);
			fetchNews(currentPage);
		} catch (error) {
			console.error("Error deleting news:", error);
		} finally {
			setLoading(false);
		}
	};

	const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

	return (
		<>
			<Helmet>
				<title>News List</title>
			</Helmet>

			<Container>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="h4" gutterBottom>
						News
					</Typography>
					<Button variant="contained" color="primary" onClick={handleCreateNews}>
						+ Create News
					</Button>
				</Stack>

				<Card>
					{loading ? (
						<Box sx={{ display: "flex", justifyContent: "center" }} style={{ padding: 20 }}>
							<CircularProgress />
						</Box>
					) : (
						<Scrollbar>
							<NewsTable news={news} onMenuOpen={handleOpenMenu} />
						</Scrollbar>
					)}
				</Card>

				{totalPages > 1 && (
					<Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
						<Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
					</Box>
				)}

				{/* Delete Confirmation Dialog */}
				<Dialog
					open={showDeleteDialog}
					onClose={() => setShowDeleteDialog(false)}
					aria-labelledby="delete-dialog-title"
					aria-describedby="delete-dialog-description"
				>
					<DialogTitle id="delete-dialog-title">Delete News</DialogTitle>
					<DialogContent>
						<Typography>Are you sure you want to delete this news article? This action cannot be undone.</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
						<Button onClick={confirmDeleteNews} color="error" variant="contained" disabled={loading}>
							Delete
						</Button>
					</DialogActions>
				</Dialog>

				{/* Three dots popover menu */}
				<Popover
					open={Boolean(open)}
					anchorEl={open}
					onClose={handleCloseMenu}
					anchorOrigin={{ vertical: "top", horizontal: "left" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					PaperProps={{
						sx: {
							p: 1,
							width: 160,
							"& .MuiMenuItem-root": {
								px: 1,
								typography: "body2",
								borderRadius: 0.75,
							},
						},
					}}
				>
					<MenuItem onClick={() => handleEditNews(openId)} sx={{ color: "text.secondary" }}>
						<EditIcon sx={{ mr: 2 }} />
						Edit
					</MenuItem>

					<MenuItem onClick={() => handleDeleteNews(openId)} sx={{ color: "error.main" }}>
						<DeleteIcon sx={{ mr: 2 }} />
						Delete
					</MenuItem>
				</Popover>
			</Container>
		</>
	);
}
